/**
 * useMarquees Hook
 * Custom hook to fetch and manage marquee state
 * Provides public marquee data and enabled status
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getMarquees,
  getMarqueeSettings,
} from '../services/marqueeService';
import type { MarqueePublic, MarqueeSettings } from '../types';

export interface UseMarqueesResult {
  marquees: MarqueePublic[];
  enabled: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  settings: MarqueeSettings | null;
}

/**
 * Hook to fetch and manage public marquee data
 * Automatically fetches on mount and provides refetch capability
 */
export const useMarquees = (): UseMarqueesResult => {
  const [marquees, setMarquees] = useState<MarqueePublic[]>([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [settings, setSettings] = useState<MarqueeSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMarquees = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch marquee settings to check if enabled and get default values
      const settingsResponse = await getMarqueeSettings();

      if (!settingsResponse.success || !settingsResponse.data) {
        setEnabled(false);
        setSettings(null);
        setMarquees([]);
        setError(
          new Error(settingsResponse.error?.message || 'Failed to fetch marquee settings')
        );
        setLoading(false);
        return;
      }

      const fetchedSettings = settingsResponse.data;
      const isEnabled = fetchedSettings.enabled;
      setEnabled(isEnabled);
      setSettings(fetchedSettings);

      // Only fetch marquees if feature is enabled
      if (isEnabled) {
        const marqueesResponse = await getMarquees();

        if (marqueesResponse.success && marqueesResponse.data) {
          setMarquees(marqueesResponse.data);
        } else {
          setMarquees([]);
          setError(
            new Error(marqueesResponse.error?.message || 'Failed to fetch marquees')
          );
        }
      } else {
        setMarquees([]);
      }
    } catch (err) {
      setEnabled(false);
      setSettings(null);
      setMarquees([]);
      setError(err instanceof Error ? err : new Error('Network error occurred'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarquees();
  }, [fetchMarquees]);

  return {
    marquees,
    enabled,
    loading,
    error,
    refetch: fetchMarquees,
    settings,
  };
};
